import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { EmailDetail } from './EmailDetail';
import { formatDate } from '../../utils/dateFormat';
import { api } from '../../services/api';
import { RefreshAnimation } from './RefreshAnimation';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface Email {
  id: string;
  sender: string;
  senderName?: string;
  subject: string;
  content: string;
  created_at: number;
  read: boolean;
  isNew?: boolean;
  attachments?: {
    filename: string;
    contentType: string;
    size: number;
    url: string;
  }[];
}

const EmailListItem: React.FC<{
  email: Email;
  isSelected: boolean;
  onClick: () => void;
  onSwipe?: () => void;
}> = ({ email, isSelected, onClick, onSwipe }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 100) {
      onSwipe?.();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      className={`
        cursor-pointer p-4 border-b hover:bg-gray-50 transition-all duration-200
        ${isSelected ? 'bg-primary-light/10 border-primary-light/20' : 'bg-white border-gray-100'}
        ${!email.read ? 'shadow-sm' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {/* 发件人信息 */}
        <div className={`${isMobile ? 'flex-1' : 'w-[200px]'} flex-shrink-0`}>
          <div className="font-medium text-gray-800 truncate">
            {email.senderName || email.sender}
          </div>
        </div>

        {/* 在非移动端显示分隔符和主题 */}
        {!isMobile && (
          <>
            <div className="text-gray-300 flex-shrink-0">|</div>
            <div className="flex-1 text-gray-700 truncate font-normal">
              {email.subject || <span className="text-gray-400 italic">{t('sections.common.inbox.noSubject')}</span>}
            </div>
          </>
        )}

        {/* 时间戳 */}
        <div className={`${isMobile ? 'ml-auto' : 'w-[100px]'} text-right flex-shrink-0`}>
          <span className="text-sm text-gray-500">
            {formatDate(email.created_at)}
          </span>
        </div>

        {/* 未读标记 */}
        {!email.read && (
          <div className="w-2 h-2 rounded-full bg-primary-dark flex-shrink-0 ml-2" />
        )}
      </div>
    </motion.div>
  );
};

export const EmailList: React.FC<{ emailAddress: string }> = ({ emailAddress }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const AUTO_REFRESH_INTERVAL = 5000; // 自动刷新间隔（10秒）
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // 从 localStorage 加载邮件和已读状态，并检查过期时间
  useEffect(() => {
    const savedEmailsData = localStorage.getItem('emails');
    const savedEmailsTimestamp = localStorage.getItem('emailsTimestamp');
    
    if (savedEmailsData && savedEmailsTimestamp) {
      const currentTime = Date.now();
      const storedTime = parseInt(savedEmailsTimestamp);
      const timeLimit = 24 * 60 * 60 * 1000; // 24小时的毫秒数
      
      // 检查是否超过24小时
      if (currentTime - storedTime < timeLimit) {
        setEmails(JSON.parse(savedEmailsData));
      } else {
        // 如果超过24小时，清除本地存储
        localStorage.removeItem('emails');
        localStorage.removeItem('emailsTimestamp');
        setEmails([]);
      }
    }
  }, []);

  // 保存邮件到 localStorage，同时保存时间戳
  useEffect(() => {
    if (emails.length > 0) {
      localStorage.setItem('emails', JSON.stringify(emails));
      localStorage.setItem('emailsTimestamp', Date.now().toString());
    }
  }, [emails]);

  // 自动刷新邮件（静默）
  const autoFetchEmails = useCallback(async () => {
    if (!emailAddress) return;

    try {
      const response = await api.getEmails(emailAddress);
      const newEmails = await response.json();
      setEmails(prev => {
        const existingEmailsMap = new Map(
          prev.map(email => [email.id, { read: email.read, isNew: email.isNew }])
        );

        const mergedEmails = newEmails.map((email: Omit<Email, 'read' | 'isNew'>) => ({
          ...email,
          read: existingEmailsMap.has(email.id) 
            ? existingEmailsMap.get(email.id)!.read 
            : false,
          isNew: !existingEmailsMap.has(email.id)
        }));

        return mergedEmails;
      });
    } catch (error) {
      console.error('Failed to auto-fetch emails:', error);
    }
  }, [emailAddress]);

  // 处理手动刷新冷却
  useEffect(() => {
    if (refreshCooldown <= 0) return;

    const timer = setInterval(() => {
      setRefreshCooldown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [refreshCooldown]);

  // 自动刷新定时器
  useEffect(() => {
    if (!emailAddress) return;

    // 初始加载
    autoFetchEmails();
    
    // 设置定时器
    const interval = setInterval(autoFetchEmails, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [emailAddress, autoFetchEmails]);

  const handleClearAll = async () => {
    if (isDeleting || !emails.length) return;

    try {
      setIsDeleting(true);
      await api.deleteAllEmails(emailAddress);
      
      // 清空邮件列表的同时，清除本地存储
      setEmails([]);
      setSelectedEmailId(null);
      localStorage.removeItem('emails');
      localStorage.removeItem('emailsTimestamp');
      
      toast.success(t('sections.common.inbox.deleteSuccess'));
      
      // 可选：触发一次刷新以确保状态同步
      autoFetchEmails();
    } catch (error) {
      console.error('Failed to delete emails:', error);
      toast.error(t('sections.common.inbox.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEmailClick = (emailId: string) => {
    // 如果点击的是当前选中的邮件，则关闭它
    if (selectedEmailId === emailId) {
      setSelectedEmailId(null);
    } else {
      // 否则选中新的邮件
      setSelectedEmailId(emailId);
      setEmails(prev =>
        prev.map(email =>
          email.id === emailId
            ? { ...email, read: true, isNew: false }
            : email
        )
      );
    }
  };

  // 添加滑动删除处理函数
  const handleSwipeDelete = useCallback(async (emailId: string) => {
    try {
      // 先更新本地状态
      setEmails(prev => prev.filter(email => email.id !== emailId));
      
      // 然后调用远程 API
      await api.deleteAllEmails(emailId);
    } catch (error) {
      console.error('Failed to delete email:', error);
      // 如果删除失败，可以选择恢复本地状态或显示错误消息
      // TODO: 添加错误处理逻辑
    }
  }, []);

  // 刷新按钮组件
  const RefreshButton = () => {
    const [isSpinning, setIsSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);

    const handleClick = () => {
      if (isSpinning) return;
      setIsSpinning(true);
      setRotation(prev => prev + 360); // 增加360度
      setTimeout(() => setIsSpinning(false), 3000);
    };

    return (
      <div className="relative">
        <motion.button
          onClick={handleClick}
          whileTap={{ scale: 0.95 }}
          className={`
            p-2 rounded-lg transition-colors relative
            ${isSpinning ? 'text-primary-dark' : 'text-gray-600 hover:text-primary-dark'}
            hover:bg-gray-100/50
          `}
        >
          <motion.div
            animate={{ rotate: rotation }}
            transition={{
              duration: 3,
              ease: "linear",
            }}
          >
            <ArrowPathIcon className="w-5 h-5" />
          </motion.div>
        </motion.button>
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Inbox Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
        <h2 className="font-semibold text-lg">{t('sections.common.inbox.title')}</h2>
        <div className="flex items-center space-x-2">
          <RefreshButton />
          <motion.button
            onClick={handleClearAll}
            disabled={isDeleting || !emails.length}
            className={`p-2 rounded-lg transition-colors relative
              ${isDeleting || !emails.length 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:text-primary-dark hover:bg-gray-100/50'
              }`}
          >
            <TrashIcon className="w-5 h-5" />
          </motion.button>
        </div>
      </div>

      {/* 刷新动画区域 */}
      <div className="flex items-center justify-center py-3 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
        <RefreshAnimation />
        <span className="ml-2 text-sm text-gray-500">
          {t('sections.common.inbox.refreshing')}
        </span>
      </div>

      {/* Email List Content - 优化移动端滚动 */}
      <div className="flex-1 overflow-hidden">
        <div 
          className={`
            p-4 overflow-y-auto overflow-x-hidden
            ${isMobile 
              ? 'h-[calc(100vh-12rem)] min-h-[15rem]' 
              : 'h-[calc(100vh-16rem)] min-h-[30rem]'
            }
            max-h-[40rem]
            touch-pan-y
          `}
        >
          {emails.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t('sections.common.inbox.empty')}
            </div>
          ) : (
            <div className="space-y-1">
              {emails.map((email) => (
                <React.Fragment key={email.id}>
                  <EmailListItem
                    email={email}
                    isSelected={selectedEmailId === email.id}
                    onClick={() => handleEmailClick(email.id)}
                    onSwipe={() => handleSwipeDelete(email.id)}
                  />
                  
                  <AnimatePresence>
                    {selectedEmailId === email.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <EmailDetail
                          email={email}
                          onClose={() => setSelectedEmailId(null)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 